/**
 * The typed client. `AppRouter` is imported as a *type* from the server's source, so every
 * procedure name, input and output on this side is checked against the real implementation and
 * the import disappears at build time — no generated client, no server code in the bundle.
 */

import { httpBatchLink } from '@trpc/client';
import { createTRPCReact } from '@trpc/react-query';
import { API_BASE_URL } from '@/lib/api';
import { captureSessionId, withNativeAuth } from '@/native/session';
import type { AppRouter } from '../../../server/src/trpc/router.js';

export const trpc = createTRPCReact<AppRouter>();

export function createTRPCClient() {
  return trpc.createClient({
    links: [
      httpBatchLink({
        // Relative unless told otherwise: in dev Vite proxies /trpc to :4380, in production the
        // API is served from the same origin, and either way the request is same-origin to the
        // browser — which is what lets the httpOnly session cookie ride along. A native shell has
        // no same origin and sets `VITE_API_URL`; the cookie cannot survive that trip at all, so
        // the shell carries the same session id in an `Authorization: Bearer` header instead —
        // see `@/native/session`. Both helpers below are no-ops in a browser, so the web request
        // is byte for byte the one this client has always made.
        url: `${API_BASE_URL}/trpc`,
        async fetch(url, options) {
          const response = await fetch(url, {
            ...options,
            credentials: 'include',
            headers: withNativeAuth(options?.headers),
          });
          // A response that minted a session carries the id the shell has to keep. Read before
          // the body is touched, and awaited so the next request already has it.
          await captureSessionId(response);
          return response;
        },
      }),
    ],
  });
}
